import React, { useCallback, useContext } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { toast } from "react-toastify";

import { CartContext } from "../../contexts/CartContext";
import { ExtendedCartMerchandise } from "../../types/shopify-components";
import { secondaryButton, primaryButton } from "../../utils/styles";
import LocaleLink from "../LocaleLink";

export const TEST_ID_TOAST = "toast";
export const TEST_ID_ADD_TO_CHECKOUT = "ecommerce-product-actions-add-to-cart";
export const TEST_ID_BACK_TO_SHOP_BUTTON =
  "ecommerce-product-actions-back-to-shop-button";

interface EcommerceProductActionsProps {
  isChangingToProductsPage: boolean;
  shouldNotify: boolean;
  merchandises: ExtendedCartMerchandise[];
}

const EcommerceProductActions: React.FC<EcommerceProductActionsProps> = ({
  isChangingToProductsPage,
  shouldNotify,
  merchandises,
}) => {
  const { formatMessage } = useIntl();
  const {
    localItems,
    isCreating,
    hasMerchandise,
    isAddingLines,
    isRemovingLines,
    isTouched,
    update,
  } = useContext(CartContext);
  const productsSlug = formatMessage({ id: "appPath.products" });

  const handleUpdateClick = useCallback(async () => {
    const quantities = localItems.reduce<number[]>((acc, item) => {
      if (
        merchandises.some(merchandise => merchandise.id === item.merchandise.id)
      ) {
        acc.push(item.input.quantity ?? 0);
        return acc;
      }
      return acc;
    }, []);

    if (shouldNotify) {
      toast.info(
        <p className="ma0" data-testid={TEST_ID_TOAST}>
          <FormattedMessage id="calendar.selectDateToSeeProducts" />
        </p>,
        {
          toastId: "toast-select-date-to-see-products",
        },
      );
    } else if (!isTouched && quantities.every(quantity => quantity === 0)) {
      toast.info(
        <p className="ma0" data-testid={TEST_ID_TOAST}>
          <FormattedMessage id="product.selectAmountOfProductsToAdd" />
        </p>,
        {
          toastId: "toast-select-amount-of-products-to-add",
        },
      );
    } else {
      await update({ openMiniCart: true });
    }
  }, [localItems, isTouched, shouldNotify, update, merchandises]);

  const actingOnProduct = isCreating || isAddingLines || isRemovingLines;
  const hasVariantInCheckout = merchandises.some(({ id }) =>
    hasMerchandise(id, true),
  );

  return (
    <div className="flex flex-column flex-row-l justify-between">
      <button
        disabled={actingOnProduct}
        className={`${secondaryButton} mb3 mb0-l order-2-l`}
        onClick={handleUpdateClick}
        data-testid={TEST_ID_ADD_TO_CHECKOUT}
      >
        {actingOnProduct ? (
          <FormattedMessage id="status.updatingProductToCart" />
        ) : hasVariantInCheckout ? (
          <FormattedMessage id="product.updateCart" />
        ) : (
          <FormattedMessage id="product.addToCart" />
        )}
      </button>
      <LocaleLink slug={productsSlug}>
        <a
          className={`${primaryButton} order-1-l`}
          data-testid={TEST_ID_BACK_TO_SHOP_BUTTON}
        >
          <FormattedMessage
            id={
              isChangingToProductsPage
                ? "product.loading"
                : "product.backToShop"
            }
          />
        </a>
      </LocaleLink>
    </div>
  );
};

export default EcommerceProductActions;
